import React from "react";
import Modal from "./Modal";
import { toast } from "sonner";
import { Download, MessageSquare, Mail, Share2, Copy, QrCode } from "lucide-react";
import { downloadVCard, cardToText, buildVCard } from "../lib/vcard";

export default function ShareDialog({ open, onClose, card }) {
  if (!card) return null;

  const text = cardToText(card);
  const smsBody = encodeURIComponent(text);
  const emailBody = encodeURIComponent(text);
  const emailSubject = encodeURIComponent(`Contact: ${card.name || card.company}`);

  const webShare = async () => {
    try {
      const file = new File([buildVCard(card)], `${(card.name || "contact")}.vcf`, {
        type: "text/vcard",
      });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: card.name, text });
      } else if (navigator.share) {
        await navigator.share({ title: card.name || "Contact", text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Contact copied to clipboard");
      }
    } catch (e) {
      /* user cancelled */
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(text);
    toast.success("Contact details copied");
  };

  const qrData = encodeURIComponent(buildVCard(card));
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${qrData}`;

  const btn =
    "flex flex-col items-center justify-center gap-2 p-4 rounded-xl bg-[#1E293B] border border-slate-700 hover:border-blue-500 hover:bg-slate-800 transition-colors text-center";

  return (
    <Modal open={open} onClose={onClose} maxWidth="max-w-md" testId="share-dialog">
      <div className="p-6">
        <p className="overline text-blue-500 mb-1">Send to someone</p>
        <h2 className="font-heading text-2xl font-bold tracking-tight mb-1">
          Share {card.name || "contact"}
        </h2>
        <p className="text-sm text-slate-400 mb-6">
          They can save it straight into their phone contacts.
        </p>

        <div className="flex justify-center mb-6">
          <div className="p-3 bg-white rounded-xl">
            <img src={qrUrl} alt="QR code" className="w-40 h-40" data-testid="share-qr-code" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button data-testid="download-vcard-button" onClick={() => { downloadVCard(card); toast.success("vCard downloaded"); }} className={btn}>
            <Download className="w-5 h-5 text-blue-500" />
            <span className="text-xs font-medium">Save .vcf</span>
          </button>
          <a data-testid="send-sms-button" href={`sms:?&body=${smsBody}`} className={btn}>
            <MessageSquare className="w-5 h-5 text-emerald-400" />
            <span className="text-xs font-medium">Text (SMS)</span>
          </a>
          <a data-testid="send-email-button" href={`mailto:?subject=${emailSubject}&body=${emailBody}`} className={btn}>
            <Mail className="w-5 h-5 text-indigo-400" />
            <span className="text-xs font-medium">Email</span>
          </a>
          <button data-testid="web-share-button" onClick={webShare} className={btn}>
            <Share2 className="w-5 h-5 text-blue-500" />
            <span className="text-xs font-medium">More…</span>
          </button>
        </div>

        <button
          data-testid="copy-contact-button"
          onClick={copy}
          className="w-full mt-3 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-slate-700 text-slate-300 hover:text-white hover:border-slate-500 text-sm transition-colors"
        >
          <Copy className="w-4 h-4" /> Copy details
        </button>
      </div>
    </Modal>
  );
}
